import LegalLayout, { Section } from "../components/LegalLayout";

export default function TermsPage() {
  return (
    <LegalLayout
      title="Terms &amp; Conditions"
      lastUpdated="February 2026"
      testid="terms-page"
      intro="By using Feel Greats, you agree to these terms. We've kept them as plain and fair as we could — please read them carefully."
    >
      <Section heading="1. About Feel Greats">
        <p>
          Feel Greats (the "Service", "we", "us") is a wellness assessment and recommendation
          platform that helps you understand your health profile and discover Unicity Feel Great
          System products that may support your wellness goals. The Service is provided from the
          United Kingdom by the operators of{" "}
          <a className="text-emerald-700 font-semibold" href="https://feelgreats.co.uk">
            https://feelgreats.co.uk
          </a>
          .
        </p>
      </Section>

      <Section heading="2. Wellness, not medical advice">
        <p>
          <strong>
            Feel Greats provides general wellness information and educational guidance only. It
            does not provide medical advice, diagnosis or treatment.
          </strong>{" "}
          Our recommendations are intended to support healthy lifestyle choices alongside — not
          in place of — professional medical care.
        </p>
        <p>
          Always consult a qualified healthcare professional before starting any new dietary,
          fitness or supplement regimen, especially if you are pregnant, nursing, taking
          prescription medication or managing a chronic condition. Never disregard or delay
          seeking professional medical advice because of something you read on Feel Greats.
        </p>
        <p>
          If you experience a medical emergency, contact your local emergency services
          immediately.
        </p>
      </Section>

      <Section heading="3. Eligibility">
        <p>
          You must be at least 18 years old to create an account and to purchase any products
          through Feel Greats. By signing up you confirm that the information you provide is
          accurate and that you are using the Service for personal, non-commercial purposes.
        </p>
      </Section>

      <Section heading="4. Your account">
        <p>
          You're responsible for keeping your login credentials confidential and for all activity
          on your account. Notify us immediately at{" "}
          <a className="text-emerald-700 font-semibold" href="mailto:support@feelgreats.co.uk">
            support@feelgreats.co.uk
          </a>{" "}
          if you suspect any unauthorised access. We reserve the right to suspend or close
          accounts that violate these terms or compromise the integrity of the Service.
        </p>
      </Section>

      <Section heading="5. Recommendations &amp; results">
        <p>
          Recommendations are generated using a combination of rule-based logic and AI. They are
          provided for educational and wellness support purposes and are not guaranteed to
          produce specific outcomes. Real-world results vary based on factors including (but not
          limited to) consistency, lifestyle, genetics and overall health.
        </p>
        <p>
          Any timeframes mentioned (for example, "you may notice steadier energy within 14–30
          days") describe typical lifestyle outcomes — they are not promises and they do not
          constitute claims to treat, cure, prevent or diagnose any disease.
        </p>
      </Section>

      <Section heading="6. Purchases &amp; subscriptions">
        <p>
          Payments are processed by Stripe in pounds sterling (GBP). When you complete a
          purchase, you agree to the price shown at checkout (including any applicable
          discounts). For subscriptions, you authorise us to charge your payment method monthly
          at the then-current price until you cancel.
        </p>
        <p>
          You can pause or cancel a subscription at any time from your account or by emailing{" "}
          <a className="text-emerald-700 font-semibold" href="mailto:support@feelgreats.co.uk">
            support@feelgreats.co.uk
          </a>
          . Cancellation stops future billing; it does not refund prior periods (see our{" "}
          <a className="text-emerald-700 font-semibold" href="/refund">
            Refund Policy
          </a>
          ).
        </p>
      </Section>

      <Section heading="7. Coupons, referrals &amp; bonus credits">
        <p>
          Coupon codes and referral codes are subject to limits stated at checkout. Each
          referral coupon may not be redeemed by its owner, may have a maximum-redemption cap,
          and is non-transferable. Bonus credits earned from successful referrals have no cash
          value and may not be exchanged or sold. We reserve the right to reverse referral
          rewards in cases of abuse or fraud.
        </p>
      </Section>

      <Section heading="8. Intellectual property">
        <p>
          The Feel Greats name, branding, content, code and design are owned by us or our
          licensors and protected by intellectual-property laws. You may not copy, modify or
          redistribute any part of the Service without our written permission. Unicity products
          and trademarks remain the property of Unicity International.
        </p>
      </Section>

      <Section heading="9. Acceptable use">
        <p>You agree not to:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Submit false, misleading or harmful information</li>
          <li>Attempt to bypass paywalls, coupons, rate limits or security mechanisms</li>
          <li>Scrape, reverse-engineer or interfere with the Service</li>
          <li>Use the Service for unlawful purposes or to harass others</li>
        </ul>
      </Section>

      <Section heading="10. Liability">
        <p>
          To the maximum extent permitted by law, Feel Greats and its operators are not liable
          for indirect, incidental or consequential damages arising from your use of the
          Service. Our total liability for any claim is limited to the amount you paid us in the
          12 months preceding the claim. Nothing in these terms excludes liability that cannot
          legally be excluded (for example, fraud or gross negligence).
        </p>
      </Section>

      <Section heading="11. Changes &amp; termination">
        <p>
          We may update the Service or these terms from time to time. Material changes will be
          communicated by email or by a prominent notice on the site. Continued use of the
          Service after a change means you accept the updated terms.
        </p>
      </Section>

      <Section heading="12. Governing law">
        <p>
          These terms are governed by the laws of England and Wales. Disputes will be resolved in
          the competent courts of England and Wales, except where mandatory consumer-protection
          law gives you the right to bring a claim elsewhere.
        </p>
      </Section>
    </LegalLayout>
  );
}
